/**
 * Org-local date resolution — the single most demo-critical piece of the MCP surface
 * (mcp-server-design §1.2 / §6.1 step 3). MCP date args are `YYYY-MM-DD` interpreted in the
 * ORG's IANA timezone; the server resolves them to UTC instants. This is what makes "what's
 * on tomorrow?" correct in the 8–10 PM ET window where UTC-tomorrow ≠ NY-tomorrow.
 *
 * Pure + dependency-free: we derive a zone's UTC offset at a given wall-clock instant via
 * `Intl.DateTimeFormat` (DST-correct, no tz database to ship), then invert it to get the UTC
 * instant for a local midnight. Range is [start-of-from-day, start-of-day-AFTER-to-day).
 */

/**
 * The zone an org row falls back to when it somehow has none — matching the column default
 * (`packages/db/src/schema/auth.ts`, core-data-model Q6).
 *
 * ⚠️ A fallback is not a clock. Reaching for this means the org row was missing or unread,
 * and silently rendering a Phoenix shop's schedule on New York time is the exact defect
 * ticket 023 exists to remove. Server-side it is the last resort behind a real read; on the
 * client it must never stand in for a timezone that simply has not LOADED yet — wait for it.
 */
export const DEFAULT_TIMEZONE = 'America/New_York';

/** Milliseconds a zone is ahead of UTC at the given absolute instant (DST-aware). */
function zoneOffsetMs(timeZone: string, atUtcMs: number): number {
  // Format the same instant in the target zone, read the wall-clock parts back, and treat
  // them as if they were UTC — the difference from the real instant is the offset.
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(atUtcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - atUtcMs;
}

/** The zone-local wall-clock [hour, minute, second] for an absolute instant. */
function zoneHms(timeZone: string, atUtcMs: number): [number, number, number] {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(atUtcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return [get('hour'), get('minute'), get('second')];
}

/**
 * The UTC instant that begins `YYYY-MM-DD` in `timeZone` — normally local midnight, but
 * DST-boundary-correct for BOTH edges:
 *   - fall-back (00:00 occurs twice): the EARLIER of the two midnights (start of day);
 *   - spring-forward AT midnight (00:00 doesn't exist): the first valid instant of that day
 *     (the transition instant, e.g. 01:00 local) — never the previous calendar day.
 * Two offset probes bracket the boundary; we then pick the candidate that actually reads the
 * requested day at 00:00, or (in a gap) the earliest instant still on the requested day. US
 * zones transition at 02:00 so this reduces to plain local midnight, but it is now correct for
 * any org timezone (e.g. America/Santiago, America/Havana) as well.
 */
export function localMidnightUtc(dateYmd: string, timeZone: string): Date {
  const [y, m, d] = dateYmd.split('-').map(Number);
  const utcGuess = Date.UTC(y!, m! - 1, d!, 0, 0, 0);
  const off1 = zoneOffsetMs(timeZone, utcGuess);
  const candidate = utcGuess - off1;
  const off2 = zoneOffsetMs(timeZone, candidate);
  if (off1 === off2) return new Date(candidate);

  const cands = [candidate, utcGuess - off2];
  const onDay = cands.filter((i) => localDateOf(new Date(i), timeZone) === dateYmd);
  const midnights = onDay.filter((i) => {
    const [h, mm] = zoneHms(timeZone, i);
    return h === 0 && mm === 0;
  });
  if (midnights.length) return new Date(Math.min(...midnights)); // fall-back → earliest 00:00
  if (onDay.length) return new Date(Math.min(...onDay)); // gap → first valid instant of the day
  return new Date(candidate);
}

/**
 * The UTC instant at which `minutesFromMidnight` reads on the clock in `timeZone`, on
 * `dateYmd` — what a drag-to-time board needs to turn "the 2pm column" into an instant.
 *
 * ⭐ Adding minutes to `localMidnightUtc` is the obvious version and it is WRONG twice a
 * year: it assumes one offset for the whole day, so every wall-clock time after a DST
 * transition lands an hour out — LATE on spring-forward (the clocks jumped forward, so
 * fixed elapsed time overshoots) and EARLY on fall-back. The board reads its columns with
 * `Intl` (true wall clock) and would then write back an instant that disagrees with the
 * column the card was dropped on — a visit that moves itself an hour when you drop it.
 *
 * It is the shop's one wall-clock resolver (`wallClockUtcMs` below), so it is correct at
 * both edges, in every zone, and agrees with "the same time, another day":
 *   - fall-back (a wall time occurs twice): the EARLIER instant, matching the "start of day"
 *     convention above;
 *   - spring-forward (a wall time does not exist): the transition instant itself, so a drop
 *     into the missing hour books the first real moment after it (02:30 in New York on
 *     8 March 2026 is 03:00 EDT) rather than silently landing on the previous day.
 *
 * `minutesFromMidnight` may carry a fraction (seconds and milliseconds ride in it, to the
 * millisecond), and may run past either end of the day into the next or the previous date.
 */
export function localWallTimeUtc(
  dateYmd: string,
  minutesFromMidnight: number,
  timeZone: string,
): Date {
  return new Date(
    wallClockUtcMs(dateYmd, Math.round(minutesFromMidnight * 60_000), timeZone),
  );
}

/**
 * Resolve an inclusive `YYYY-MM-DD` .. `YYYY-MM-DD` local date range to a half-open UTC
 * instant window [startUtc, endUtc): endUtc is local midnight of the day AFTER `dateTo`, so
 * the whole `dateTo` day is included. Use `scheduledStartAt >= startUtc AND < endUtc`.
 */
export function resolveLocalDateRange(
  dateFrom: string,
  dateTo: string,
  timeZone: string,
): { startUtc: Date; endUtc: Date } {
  return {
    startUtc: localMidnightUtc(dateFrom, timeZone),
    endUtc: localMidnightUtc(addDaysYmd(dateTo, 1), timeZone),
  };
}

/** Add N days to a `YYYY-MM-DD` string (UTC-noon anchored so DST never shifts the date). */
export function addDaysYmd(dateYmd: string, days: number): string {
  const [y, m, d] = dateYmd.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!, 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** The org-local calendar date (`YYYY-MM-DD`) for an absolute instant. */
export function localDateOf(atUtc: Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return dtf.format(atUtc); // en-CA → YYYY-MM-DD
}

/**
 * What the clock on the wall reads in `timeZone` at an absolute instant: the local calendar
 * date, hour (0–23) and minute. The monitor schedule buckets by this (ops insights Phase 6b):
 * "the 7 AM check" is a question about the shop's wall clock, never about elapsed time, so a
 * DST change moves the instant and leaves the reading alone.
 */
export function localClockOf(
  atUtc: Date,
  timeZone: string,
): { date: string; hour: number; minute: number } {
  const [hour, minute] = zoneHms(timeZone, atUtc.getTime());
  return { date: localDateOf(atUtc, timeZone), hour, minute };
}

/**
 * Invert a zone-local wall clock (`YYYY-MM-DD` + milliseconds from its midnight) back to the
 * UTC instant. The ONE wall-clock → UTC rule: `localWallTimeUtc`, `shiftLocalDays` and
 * `shiftLocalMonths` all resolve through it, so a business-day deadline, a snooze's end, a
 * monitor's freshness line and a dropped visit can never disagree about a DST day.
 *
 * The zone's offset is read 14 hours either side of the wall clock (wider than any zone's
 * offset), which gives the one offset in force, or the two either side of a change that day:
 *  - an ordinary wall time: the one instant;
 *  - a wall time that happens TWICE (fall-back): the EARLIER instant, in every zone (the old
 *    two-probe versions took the later one east of UTC, e.g. Berlin);
 *  - a wall time that does NOT exist (spring-forward gap, 02:30 in New York on 8 March 2026):
 *    the first real instant at or after it, which is the change itself (03:00 EDT). One old
 *    version stepped BACK to 01:30, another pushed forward by the gap's length to 03:30.
 * What a dispatcher means by "same time, Thursday".
 */
function wallClockUtcMs(ymd: string, msFromMidnight: number, timeZone: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  // `zoneOffsetMs` reads whole seconds: resolve the whole second, then put the rest back (a
  // wall time in a gap has no rest: every moment of it maps to the change).
  const subSecond = ((msFromMidnight % 1000) + 1000) % 1000;
  const utcGuess = Date.UTC(y!, m! - 1, d!) + msFromMidnight - subSecond;
  const SPAN = 14 * 3_600_000;
  const offBefore = zoneOffsetMs(timeZone, utcGuess - SPAN);
  const offAfter = zoneOffsetMs(timeZone, utcGuess + SPAN);
  const reads = (instant: number) =>
    zoneOffsetMs(timeZone, instant) + instant === utcGuess;
  const found = [utcGuess - offBefore, utcGuess - offAfter].filter(reads);
  if (found.length > 0) return Math.min(...found) + subSecond;
  // A gap: the offset changes between the two candidates. The change itself is the first
  // instant past the missing wall time; find it to the second (`zoneOffsetMs` reads whole
  // seconds, and every zone changes on one).
  let lo = Math.min(utcGuess - offBefore, utcGuess - offAfter);
  let hi = Math.max(utcGuess - offBefore, utcGuess - offAfter);
  const startOffset = zoneOffsetMs(timeZone, lo);
  while (hi - lo > 1000) {
    const mid = lo + Math.floor((hi - lo) / 2000) * 1000;
    if (zoneOffsetMs(timeZone, mid) === startOffset) lo = mid;
    else hi = mid;
  }
  return hi;
}

/**
 * The same time of day, N calendar days later — read in the SHOP's timezone.
 *
 * This is what "move Tuesday's work to Thursday" means, and why it is not `+ 48 hours`:
 * across a DST change 48 hours lands an 8 AM start at 7 or 9 AM, so a whole day of van
 * routes quietly slips an hour. The wall clock is the promise the customer was given; the
 * instant is derived from it, never the other way round.
 *
 * Negative `days` moves work earlier. Zero is a legal no-op here — the caller decides
 * whether that's worth a write.
 */
export function shiftLocalDays(atUtc: Date, days: number, timeZone: string): Date {
  const target = addDaysYmd(localDateOf(atUtc, timeZone), days);
  return new Date(wallClockUtcMs(target, wallSecondsMs(atUtc, timeZone), timeZone));
}

/**
 * The same time of day, N calendar MONTHS later (negative: earlier), read in the SHOP's zone.
 * "12 months ago" is a question about the shop's calendar, not 365 days of elapsed time: it is
 * 9:30 AM on the same date last year, whatever the DST changes in between. A day the target
 * month does not have is clamped to its last day (31 March − 1 month = 28 or 29 February), so
 * the answer never spills into the month after.
 */
export function shiftLocalMonths(atUtc: Date, months: number, timeZone: string): Date {
  const [y, m, d] = localDateOf(atUtc, timeZone).split('-').map(Number) as [
    number,
    number,
    number,
  ];
  const total = y * 12 + (m - 1) + months;
  const ty = Math.floor(total / 12);
  const tm = total - ty * 12 + 1;
  const lastDay = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  const target = `${ty}-${String(tm).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`;
  // The milliseconds too (MAP 249 ticket 06, Codex 06-r2 #3): the wall clock is read to the whole
  // second, and a deadline compared with a stamp must not come out up to a second early. They go
  // IN to the resolver (Codex 06-r3 #5), so a wall time in a spring-forward gap, which resolves to
  // the change itself, drops them as every moment of the gap does.
  return new Date(
    wallClockUtcMs(
      target,
      wallSecondsMs(atUtc, timeZone) + atUtc.getUTCMilliseconds(),
      timeZone,
    ),
  );
}

/** The wall clock's time of day at an instant, to the whole second, in ms from midnight. */
function wallSecondsMs(atUtc: Date, timeZone: string): number {
  const [h, mi, s] = zoneHms(timeZone, atUtc.getTime());
  return ((h * 60 + mi) * 60 + s) * 1000;
}

/** UTC ms at midnight of a `YYYY-MM-DD` (month is decremented — Date.UTC months are 0-based).
 *  Using the raw split without −1 shifts every date forward a month and silently miscounts
 *  cross-month day spans (a RANGE_TOO_WIDE bypass) — so all Y-M-D → epoch math goes here. */
export function ymdToUtcMs(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

/** Inclusive whole-day count between two `YYYY-MM-DD` dates (dateTo - dateFrom + 1). */
export function inclusiveDaySpan(dateFrom: string, dateTo: string): number {
  return Math.floor((ymdToUtcMs(dateTo) - ymdToUtcMs(dateFrom)) / 86_400_000) + 1;
}

/** Format an absolute instant as an org-local ISO-8601 string WITH offset (LLM-legible). */
export function localIso(atUtc: Date | null, timeZone: string): string | null {
  if (!atUtc) return null;
  const offMs = zoneOffsetMs(timeZone, atUtc.getTime());
  const sign = offMs >= 0 ? '+' : '-';
  const abs = Math.abs(offMs);
  const oh = String(Math.floor(abs / 3_600_000)).padStart(2, '0');
  const om = String(Math.floor((abs % 3_600_000) / 60_000)).padStart(2, '0');
  const local = new Date(atUtc.getTime() + offMs);
  return `${local.toISOString().slice(0, 19)}${sign}${oh}:${om}`;
}
